import { Style, Circle as CircleStyle, Fill, Stroke } from 'ol/style.js';
import WebGLVectorLayer from 'ol/layer/WebGLVector.js';
import type VectorSource from 'ol/source/Vector.js';
import type Feature from 'ol/Feature.js';
import type OlMap from 'ol/Map.js';
import type { DataFeature, DataLayer } from '../shared/data-layer.js';
import { dataFeatureStyle } from '../shared/data-layer-fields.js';

// Below this size the Canvas renderer avoids the cost of GPU buffer creation.
const MIN_GPU_POINTS = 10_000;
let webglAvailable: boolean | undefined;

function supportsWebGL(): boolean {
  if (webglAvailable !== undefined) return webglAvailable;
  const canvas = document.createElement('canvas');
  const context = canvas.getContext('webgl');
  webglAvailable = !!context;
  context?.getExtension('WEBGL_lose_context')?.loseContext();
  return webglAvailable;
}

export function isLargeCircleLayer(layer: DataLayer, features: DataFeature[]): boolean {
  return layer.featureCount >= MIN_GPU_POINTS && !layer.display?.showLabels && !layer.style.icon
    && features.length > 0 && features.every(feature => feature.geometry?.type === 'Point' && !feature.image && !feature.sourceStyle?.icon);
}

export function canRenderPoints(layer: DataLayer, features: DataFeature[]): boolean {
  return isLargeCircleLayer(layer, features) && supportsWebGL();
}

export function setPointProperties(feature: Feature, data: DataFeature, layer: DataLayer, selected: boolean): void {
  const style = dataFeatureStyle(data, layer);
  const alpha = Math.round((style.opacity ?? 1) * 255).toString(16).padStart(2, '0');
  feature.setProperties({
    pointColor: `${style.color}${alpha}`, pointRadius: style.pointRadius ?? 6,
    pointOutline: style.pointOutlineColor ?? '#ffffff', pointSelected: selected ? 1 : 0,
  }, true);
}

export function createPointRenderer(source: VectorSource, visible: boolean): WebGLVectorLayer<VectorSource> {
  return new WebGLVectorLayer({ source, visible, style: [
    { filter: ['==', ['get', 'pointSelected'], 1], style: {
      'circle-radius': 10, 'circle-fill-color': '#ffffff', 'circle-stroke-color': '#2563eb', 'circle-stroke-width': 2,
    } },
    { style: {
      'circle-radius': ['get', 'pointRadius'], 'circle-fill-color': ['get', 'pointColor'],
      'circle-stroke-color': ['get', 'pointOutline'], 'circle-stroke-width': 2,
    } },
  ] });
}

/** WebGL picking ignores hitTolerance. Use the spatial index for the same touch target as Canvas. */
export function pointAtPixel(source: VectorSource, map: Pick<OlMap, 'getView' | 'getCoordinateFromPixel' | 'getPixelFromCoordinate'>, pixel: number[], tolerance: number): Feature | undefined {
  const padding = 33 + tolerance;
  const corners = [[-padding, -padding], [padding, -padding], [padding, padding], [-padding, padding]]
    .map(([x, y]) => map.getCoordinateFromPixel([pixel[0] + x, pixel[1] + y]));
  const extent = [Math.min(...corners.map(p => p[0])), Math.min(...corners.map(p => p[1])), Math.max(...corners.map(p => p[0])), Math.max(...corners.map(p => p[1]))];
  const projection = map.getView().getProjection();
  const world = projection.getExtent();
  const width = world[2] - world[0];
  const center = map.getCoordinateFromPixel(pixel);
  const worldOffset = Math.floor((center[0] - world[0]) / width) * width;
  const offsets = projection.canWrapX() && source.getWrapX() ? [worldOffset - width, worldOffset, worldOffset + width] : [0];
  let closest: Feature | undefined;
  let distance = Infinity;
  for (const offset of offsets) {
    const query = [extent[0] - offset, extent[1], extent[2] - offset, extent[3]];
    for (const feature of source.getFeaturesInExtent(query)) {
      const geometry = feature.getGeometry();
      if (!geometry) continue;
      const coordinate = geometry.getClosestPoint([center[0] - offset, center[1]]);
      const point = map.getPixelFromCoordinate([coordinate[0] + offset, coordinate[1]]);
      const squared = (point[0] - pixel[0]) ** 2 + (point[1] - pixel[1]) ** 2;
      const radius = Math.max(feature.get('pointRadius'), feature.get('pointSelected') ? 10 : 0) + 1 + tolerance;
      if (squared <= radius ** 2 && squared <= distance) { closest = feature; distance = squared; }
    }
  }
  return closest;
}

/** Print maps use Canvas so GPU buffers cannot disappear during image capture. */
export function pointExportStyle(): import('ol/style/Style.js').StyleFunction {
  const styles = new Map<string, Style>();
  return feature => {
    const color = feature.get('pointColor');
    const radius = feature.get('pointRadius');
    const outline = feature.get('pointOutline');
    const key = JSON.stringify([color, radius, outline]);
    let style = styles.get(key);
    if (!style) {
      style = new Style({ image: new CircleStyle({ radius, fill: new Fill({ color }), stroke: new Stroke({ color: outline, width: 2 }) }) });
      styles.set(key, style);
    }
    return style;
  };
}
